<template>
  <div>
    <my-loader :loader="loader"></my-loader>

    <p class="text-2xl mb-6">
      {{ pageTitle }}
    </p>

    <!-- ===== Обычный приём наличных ===== -->
    <template v-if="mode === 'accept'">
      <template v-if="step === 'search'">
        <v-text-field
          v-model="orderIdInput"
          label="Номер бронирования"
          outlined
          placeholder="000042"
          hide-details="auto"
          :error-messages="searchError"
          @keyup.enter="searchReservation"
        ></v-text-field>

        <div class="d-flex flex-wrap mt-4">
          <v-btn
            color="primary"
            class="mr-3 mb-2"
            :disabled="!orderIdInput"
            @click="searchReservation"
          >
            Найти
          </v-btn>
          <v-btn
            color="secondary"
            class="mb-2"
            outlined
            @click="enterHandoverMode"
          >
            Оприходовать деньги
          </v-btn>
        </div>
      </template>

      <template v-if="step === 'payment' && reservation">
        <v-card outlined class="mb-6">
          <v-card-title>Бронирование №{{ reservation.order_id }}</v-card-title>
          <v-card-text>
            <div><b>Мероприятие:</b> {{ reservation.header }}</div>
            <div><b>Клиент:</b> {{ reservation.client.name }}</div>
            <div v-if="reservation.client.phone">
              <b>Телефон:</b> {{ reservation.client.phone }}
            </div>
            <div v-if="reservation.client.email">
              <b>Email:</b> {{ reservation.client.email }}
            </div>
            <div><b>Статус оплаты:</b> {{ reservation.status }}</div>
            <div><b>Оплачено:</b> {{ reservation.pay_sum }} ₽</div>
            <div><b>К оплате:</b> {{ reservation.pay_price }} ₽</div>
          </v-card-text>
        </v-card>

        <v-card
          v-if="incomingInvoices.length"
          outlined
          class="mb-6"
        >
          <v-card-title class="pb-2">
            Счета
          </v-card-title>
          <v-card-text class="pt-0">
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>№</th>
                  <th>Сумма</th>
                  <th>Оплата</th>
                  <th>Способ</th>
                  <th>Касса</th>
                  <th>Чек</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="invoice in incomingInvoices"
                  :key="invoice.id"
                >
                  <td>{{ invoice.id }}</td>
                  <td>{{ invoice.price }} ₽</td>
                  <td>
                    <v-chip
                      x-small
                      :color="invoice.pay ? 'success' : 'error'"
                      dark
                    >
                      {{ invoice.pay ? 'Оплачен' : 'Не оплачен' }}
                    </v-chip>
                  </td>
                  <td>{{ invoice.payment_method_display || '—' }}</td>
                  <td>{{ invoice.cashbox === 2 ? 'Наличная' : 'Безналичная' }}</td>
                  <td>
                    <div>{{ invoiceOfdLabel(invoice) }}</div>
                    <a
                      v-if="invoice.ofd_receipt_url"
                      :href="invoice.ofd_receipt_url"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Открыть чек
                    </a>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>

        <v-alert
          v-else
          type="info"
          dense
          outlined
          class="mb-6"
        >
          По бронированию входящих счетов пока нет.
        </v-alert>

        <v-alert
          v-if="!hasClientContact"
          type="error"
          dense
          outlined
          class="mb-4"
        >
          У клиента нет email и телефона — фискализация чека невозможна.
        </v-alert>

        <v-text-field
          v-model.number="amount"
          label="Сумма, ₽"
          outlined
          type="number"
          min="1"
          hide-details="auto"
          :error-messages="paymentError"
        ></v-text-field>

        <div class="d-flex flex-wrap mt-4">
          <v-btn
            color="success"
            class="mr-3 mb-2"
            :disabled="!canAcceptPayment"
            @click="acceptCashPayment"
          >
            {{ currentInvoiceId ? 'Повторить отправку в ОФД' : 'Принять наличные' }}
          </v-btn>
          <v-btn
            class="mb-2"
            @click="resetForm"
          >
            Назад
          </v-btn>
        </div>
      </template>

      <template v-if="step === 'result'">
        <v-card
          v-if="reservation && incomingInvoices.length"
          outlined
          class="mb-4"
        >
          <v-card-title class="pb-2">
            Счета по бронированию
          </v-card-title>
          <v-card-text class="pt-0">
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>№</th>
                  <th>Сумма</th>
                  <th>Оплата</th>
                  <th>Способ</th>
                  <th>Касса</th>
                  <th>Чек</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="invoice in incomingInvoices"
                  :key="invoice.id"
                >
                  <td>{{ invoice.id }}</td>
                  <td>{{ invoice.price }} ₽</td>
                  <td>
                    <v-chip
                      x-small
                      :color="invoice.pay ? 'success' : 'error'"
                      dark
                    >
                      {{ invoice.pay ? 'Оплачен' : 'Не оплачен' }}
                    </v-chip>
                  </td>
                  <td>{{ invoice.payment_method_display || '—' }}</td>
                  <td>{{ invoice.cashbox === 2 ? 'Наличная' : 'Безналичная' }}</td>
                  <td>
                    <div>{{ invoiceOfdLabel(invoice) }}</div>
                    <a
                      v-if="invoice.ofd_receipt_url"
                      :href="invoice.ofd_receipt_url"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Открыть чек
                    </a>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>

        <v-alert
          :type="resultAlertType"
          dense
          outlined
          class="mb-4"
        >
          {{ resultMessage }}
        </v-alert>

        <v-card
          v-if="receiptResult"
          outlined
          class="mb-4"
        >
          <v-card-text>
            <div><b>Счёт №{{ receiptResult.id }}</b></div>
            <div><b>Сумма:</b> {{ receiptResult.price }} ₽</div>
            <div><b>Тег кассы:</b> {{ receiptResult.cash_register_tag || '—' }}</div>
            <div><b>Статус чека:</b> {{ receiptStatusLabel }}</div>
            <div v-if="receiptResult.ofd_receipt_id">
              <b>ID в ОФД:</b> {{ receiptResult.ofd_receipt_id }}
            </div>
            <div v-if="receiptResult.ofd_fiscal_document_number">
              <b>ФД:</b> {{ receiptResult.ofd_fiscal_document_number }}
            </div>
            <div v-if="receiptResult.ofd_fiscal_storage_number">
              <b>ФН:</b> {{ receiptResult.ofd_fiscal_storage_number }}
            </div>
            <div v-if="receiptResult.ofd_fiscal_sign">
              <b>ФП:</b> {{ receiptResult.ofd_fiscal_sign }}
            </div>
            <div v-if="receiptResult.ofd_receipt_dt">
              <b>Дата фискализации:</b> {{ formatReceiptDt(receiptResult.ofd_receipt_dt) }}
            </div>
            <a
              v-if="receiptResult.ofd_receipt_url"
              :href="receiptResult.ofd_receipt_url"
              target="_blank"
              rel="noopener noreferrer"
              class="d-inline-block mt-2"
            >
              Открыть чек
            </a>
            <v-alert
              v-if="receiptResult.ofd_receipt_status === 'done' && !hasReceiptProof(receiptResult)"
              type="info"
              dense
              outlined
              class="mt-3 mb-0"
            >
              Чек выбит, но реквизиты ещё не получены. Нажмите «Обновить статус чека».
            </v-alert>
          </v-card-text>
        </v-card>

        <div class="d-flex flex-wrap">
          <v-btn
            v-if="canRefreshReceipt"
            color="primary"
            class="mr-3 mb-2"
            @click="refreshReceiptStatus"
          >
            Обновить статус чека
          </v-btn>
          <v-btn
            v-if="canRetryFiscalization"
            color="warning"
            class="mr-3 mb-2"
            @click="retryFiscalization"
          >
            Повторить отправку в ОФД
          </v-btn>
          <v-btn
            color="primary"
            class="mb-2"
            @click="resetForm"
          >
            Новый приём
          </v-btn>
        </div>
      </template>
    </template>

    <!-- ===== Оприходование ===== -->
    <template v-else-if="mode === 'handover'">
      <template v-if="step === 'search' || step === 'review'">
        <v-text-field
          v-model="orderIdInput"
          label="Номер бронирования"
          outlined
          placeholder="000042"
          hide-details="auto"
          :error-messages="searchError"
          class="mb-4"
          @keyup.enter="searchHandoverReservation"
        ></v-text-field>

        <div class="d-flex flex-wrap mb-6">
          <v-btn
            color="primary"
            class="mr-3 mb-2"
            :disabled="!orderIdInput"
            @click="searchHandoverReservation"
          >
            Найти
          </v-btn>
          <v-btn
            class="mb-2"
            @click="cancelHandover"
          >
            Отмена
          </v-btn>
        </div>

        <template v-if="step === 'review' && reservation">
          <v-card outlined class="mb-6">
            <v-card-title>Бронирование №{{ reservation.order_id }}</v-card-title>
            <v-card-text>
              <div><b>Мероприятие:</b> {{ reservation.header }}</div>
              <div><b>Клиент:</b> {{ reservation.client.name }}</div>
            </v-card-text>
          </v-card>

          <v-alert
            v-if="handoverInfoMessage"
            :type="handoverInfoType"
            dense
            outlined
            class="mb-4"
          >
            {{ handoverInfoMessage }}
          </v-alert>

          <v-card
            v-if="handoverEligibleInvoices.length"
            outlined
            class="mb-6"
          >
            <v-card-title class="pb-2">
              Наличные счета для оприходования
            </v-card-title>
            <v-card-text class="pt-0">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>№</th>
                    <th>Сумма</th>
                    <th>Тег кассы</th>
                    <th>Сумма в конверте</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="invoice in handoverEligibleInvoices"
                    :key="invoice.id"
                  >
                    <td>{{ invoice.id }}</td>
                    <td>{{ invoice.price }} ₽</td>
                    <td>{{ invoice.cash_register_tag || '—' }}</td>
                    <td style="min-width: 160px">
                      <v-text-field
                        :value="handoverCountedAmounts[invoice.id]"
                        type="number"
                        dense
                        outlined
                        hide-details="auto"
                        :error-messages="handoverAmountErrors[invoice.id] || ''"
                        label="₽"
                        class="mt-2 mb-2"
                        @input="setHandoverCountedAmount(invoice.id, $event)"
                        @keyup.enter="acceptHandoverInvoice(invoice)"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-btn
                        color="success"
                        small
                        :disabled="!canAcceptHandoverInvoice(invoice)"
                        @click="acceptHandoverInvoice(invoice)"
                      >
                        Принял
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </template>

        <v-card
          v-if="handoverAccepted.length"
          outlined
          class="mb-6"
        >
          <v-card-title class="pb-2 d-flex justify-space-between align-center">
            <span>Принято в этой сессии</span>
            <span class="text-subtitle-1 font-weight-bold">
              Итого: {{ formatMoney(handoverAcceptedTotal) }} ₽
            </span>
          </v-card-title>
          <v-card-text class="pt-0">
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Бронь</th>
                  <th>Счёт</th>
                  <th>Сумма</th>
                  <th>Был тег</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in handoverAccepted"
                  :key="item.invoiceId"
                >
                  <td>{{ item.orderId }}</td>
                  <td>{{ item.invoiceId }}</td>
                  <td>{{ formatMoney(item.price) }} ₽</td>
                  <td>{{ item.fromTag || '—' }}</td>
                  <td>
                    <v-btn
                      text
                      small
                      color="error"
                      @click="removeHandoverAccepted(item.invoiceId)"
                    >
                      Убрать
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>

        <v-alert
          v-if="handoverFinishError"
          type="error"
          dense
          outlined
          class="mb-4"
        >
          {{ handoverFinishError }}
        </v-alert>

        <div
          v-if="handoverAccepted.length"
          class="d-flex flex-wrap"
        >
          <v-btn
            color="success"
            class="mr-3 mb-2"
            @click="finishHandover"
          >
            Завершить
          </v-btn>
          <v-btn
            class="mb-2"
            @click="cancelHandover"
          >
            Отмена
          </v-btn>
        </div>
      </template>

      <template v-if="step === 'done'">
        <v-alert
          type="success"
          dense
          outlined
          class="mb-4"
        >
          Оприходование завершено.
        </v-alert>

        <v-card outlined class="mb-6">
          <v-card-text>
            <div><b>Счетов:</b> {{ handoverResult.count }}</div>
            <div><b>Общая сумма:</b> {{ formatMoney(handoverResult.total_amount) }} ₽</div>
            <div><b>Тег кассы:</b> {{ handoverResult.to_tag || operatorTag || '—' }}</div>
          </v-card-text>
        </v-card>

        <div class="d-flex flex-wrap">
          <v-btn
            color="primary"
            class="mr-3 mb-2"
            @click="startNewHandover"
          >
            Новое оприходование
          </v-btn>
          <v-btn
            class="mb-2"
            @click="exitHandoverToAccept"
          >
            К приёму наличных
          </v-btn>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import Api from '@/api/api'
import MyLoader from '@/components/MyLoader.vue'

const INVOICE_NAME = 'Оказание комплекса услуг по физической культуре и спорту'
const POLL_INTERVAL_MS = 2500
const POLL_TIMEOUT_MS = 60000
const HANDOVER_STORAGE_KEY = 'cash_handover_session_v1'

export default {
  components: { MyLoader },

  data() {
    return {
      loader: false,
      mode: 'accept',
      step: 'search',
      orderIdInput: '',
      reservation: null,
      amount: null,
      searchError: null,
      paymentError: null,
      receiptResult: null,
      resultMessage: '',
      resultAlertType: 'info',
      currentInvoiceId: null,
      paymentAccepted: false,

      handoverAccepted: [],
      handoverCountedAmounts: {},
      handoverAmountErrors: {},
      handoverInfoMessage: '',
      handoverInfoType: 'info',
      handoverFinishError: null,
      handoverResult: null,
    }
  },

  computed: {
    pageTitle() {
      return this.mode === 'handover'
        ? 'Сборы. Оприходование наличных'
        : 'Сборы. Приём наличных'
    },

    hasClientContact() {
      const client = this.reservation?.client
      if (!client) return false
      return Boolean(client.email || client.phone)
    },

    canAcceptPayment() {
      return this.hasClientContact && this.amount > 0
    },

    operatorTag() {
      return this.$store.state.auth.user?.first_name || ''
    },

    incomingInvoices() {
      const invoices = this.reservation?.invoices || []
      return invoices.filter(invoice => invoice.payment_type === 1)
    },

    acceptedInvoiceIds() {
      return new Set(this.handoverAccepted.map(item => item.invoiceId))
    },

    handoverEligibleInvoices() {
      const invoices = this.reservation?.invoices || []
      return invoices.filter((invoice) => {
        if (invoice.payment_type !== 1) return false
        if (invoice.cashbox !== 2) return false
        if (!invoice.pay) return false
        if (this.acceptedInvoiceIds.has(invoice.id)) return false
        const tag = (invoice.cash_register_tag || '').trim()
        if (tag && tag === this.operatorTag) return false
        return true
      })
    },

    handoverAlreadyMineInvoices() {
      const invoices = this.reservation?.invoices || []
      return invoices.filter((invoice) => {
        if (invoice.payment_type !== 1) return false
        if (invoice.cashbox !== 2) return false
        if (!invoice.pay) return false
        const tag = (invoice.cash_register_tag || '').trim()
        return tag && tag === this.operatorTag
      })
    },

    handoverPaidCashInvoices() {
      const invoices = this.reservation?.invoices || []
      return invoices.filter(
        invoice => invoice.payment_type === 1 && invoice.cashbox === 2 && invoice.pay,
      )
    },

    handoverAcceptedTotal() {
      return this.handoverAccepted.reduce(
        (sum, item) => sum + Number(item.price || 0),
        0,
      )
    },

    canRetryFiscalization() {
      if (!this.currentInvoiceId) return false
      const status = this.receiptResult?.ofd_receipt_status
      return status === 'fail' || status === 'FERMAERROR'
    },

    canRefreshReceipt() {
      if (!this.currentInvoiceId || !this.receiptResult?.ofd_receipt_id) return false
      const status = this.receiptResult.ofd_receipt_status
      if (status === 'wait') return true
      if (status === 'done' && !this.hasReceiptProof(this.receiptResult)) return true
      return status === 'timeout'
    },

    receiptStatusLabel() {
      const labels = {
        wait: 'Обрабатывается на ККТ',
        done: 'Выбит',
        fail: 'Ошибка ККТ',
        FERMAERROR: 'Ошибка отправки в ОФД',
        timeout: 'Статус не получен',
      }
      return labels[this.receiptResult?.ofd_receipt_status] || this.receiptResult?.ofd_receipt_status || '—'
    },
  },

  created() {
    this.restoreHandoverSession()
  },

  methods: {
    formatMoney(value) {
      const num = Number(value)
      if (Number.isNaN(num)) return value
      return num.toLocaleString('ru-RU')
    },

    amountsMatch(a, b) {
      return Math.round(Number(a) * 100) === Math.round(Number(b) * 100)
    },

    hasReceiptProof(receipt) {
      if (!receipt) return false
      return Boolean(
        receipt.ofd_receipt_url
        || receipt.ofd_fiscal_sign
        || receipt.ofd_fiscal_document_number,
      )
    },

    formatReceiptDt(value) {
      if (!value) return ''
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? value : date.toLocaleString('ru-RU')
    },

    invoiceOfdLabel(invoice) {
      if (invoice.cashbox !== 2 || !invoice.pay) return '—'
      const labels = {
        wait: 'В обработке',
        done: 'Выбит',
        fail: 'Ошибка',
        FERMAERROR: 'Не отправлен',
      }
      return labels[invoice.ofd_receipt_status] || invoice.ofd_receipt_status || '—'
    },

    async reloadReservation() {
      if (!this.orderIdInput) return
      const reservation = await Api.getReservationByOrderId({
        order_id: this.orderIdInput,
      })
      this.reservation = reservation
    },

    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },

    extractErrorMessage(error) {
      const data = error?.response?.data
      if (!data) return 'Произошла ошибка'
      return data.error || data.message || data.detail || 'Произошла ошибка'
    },

    persistHandoverSession() {
      if (this.mode !== 'handover' || this.step === 'done') {
        localStorage.removeItem(HANDOVER_STORAGE_KEY)
        return
      }
      const payload = {
        mode: 'handover',
        step: this.step === 'review' ? 'search' : this.step,
        accepted: this.handoverAccepted,
      }
      localStorage.setItem(HANDOVER_STORAGE_KEY, JSON.stringify(payload))
    },

    restoreHandoverSession() {
      try {
        const raw = localStorage.getItem(HANDOVER_STORAGE_KEY)
        if (!raw) return
        const payload = JSON.parse(raw)
        if (!payload || payload.mode !== 'handover') return
        this.mode = 'handover'
        this.step = 'search'
        this.handoverAccepted = Array.isArray(payload.accepted) ? payload.accepted : []
        this.reservation = null
        this.orderIdInput = ''
      } catch (e) {
        localStorage.removeItem(HANDOVER_STORAGE_KEY)
      }
    },

    enterHandoverMode() {
      this.mode = 'handover'
      this.step = 'search'
      this.orderIdInput = ''
      this.reservation = null
      this.searchError = null
      this.handoverInfoMessage = ''
      this.handoverFinishError = null
      this.handoverCountedAmounts = {}
      this.handoverAmountErrors = {}
      this.handoverResult = null
      this.persistHandoverSession()
    },

    cancelHandover() {
      this.handoverAccepted = []
      this.handoverCountedAmounts = {}
      this.handoverAmountErrors = {}
      this.handoverInfoMessage = ''
      this.handoverFinishError = null
      this.handoverResult = null
      this.reservation = null
      this.orderIdInput = ''
      this.searchError = null
      localStorage.removeItem(HANDOVER_STORAGE_KEY)
      this.mode = 'accept'
      this.step = 'search'
    },

    startNewHandover() {
      this.handoverAccepted = []
      this.handoverCountedAmounts = {}
      this.handoverAmountErrors = {}
      this.handoverInfoMessage = ''
      this.handoverFinishError = null
      this.handoverResult = null
      this.reservation = null
      this.orderIdInput = ''
      this.searchError = null
      this.mode = 'handover'
      this.step = 'search'
      this.persistHandoverSession()
    },

    exitHandoverToAccept() {
      this.cancelHandover()
    },

    canAcceptHandoverInvoice(invoice) {
      const counted = this.handoverCountedAmounts[invoice.id]
      if (counted === null || counted === undefined || counted === '') return false
      return !Number.isNaN(Number(counted)) && Number(counted) >= 0
    },

    setHandoverCountedAmount(invoiceId, value) {
      const parsed = value === '' || value === null || value === undefined
        ? null
        : Number(value)
      this.$set(this.handoverCountedAmounts, invoiceId, Number.isNaN(parsed) ? null : parsed)
      this.$set(this.handoverAmountErrors, invoiceId, '')
    },

    acceptHandoverInvoice(invoice) {
      this.$set(this.handoverAmountErrors, invoice.id, '')
      this.handoverFinishError = null

      const counted = this.handoverCountedAmounts[invoice.id]
      if (counted === null || counted === undefined || counted === '') {
        this.$set(this.handoverAmountErrors, invoice.id, 'Введите сумму')
        return
      }

      if (!this.amountsMatch(counted, invoice.price)) {
        this.$set(
          this.handoverAmountErrors,
          invoice.id,
          `Ожидалось ${invoice.price} ₽, в конверте ${counted} ₽`,
        )
        return
      }

      this.handoverAccepted.push({
        invoiceId: invoice.id,
        orderId: this.reservation.order_id,
        clientName: this.reservation.client?.name || '',
        eventHeader: this.reservation.header || '',
        price: invoice.price,
        fromTag: (invoice.cash_register_tag || '').trim(),
        countedAmount: Number(counted),
      })

      this.$delete(this.handoverCountedAmounts, invoice.id)
      this.$delete(this.handoverAmountErrors, invoice.id)
      this.persistHandoverSession()

      if (!this.handoverEligibleInvoices.length) {
        this.orderIdInput = ''
        this.reservation = null
        this.step = 'search'
        this.handoverInfoMessage = ''
      }
    },

    removeHandoverAccepted(invoiceId) {
      this.handoverAccepted = this.handoverAccepted.filter(
        item => item.invoiceId !== invoiceId,
      )
      this.persistHandoverSession()
    },

    async searchHandoverReservation() {
      this.searchError = null
      this.handoverInfoMessage = ''
      this.handoverInfoType = 'info'
      this.handoverFinishError = null
      this.loader = true
      try {
        const reservation = await Api.getReservationByOrderId({
          order_id: this.orderIdInput,
        })
        this.reservation = reservation
        this.step = 'review'
        this.handoverCountedAmounts = {}
        this.handoverAmountErrors = {}
        this.$nextTick(() => {
          this.handoverEligibleInvoices.forEach((invoice) => {
            this.$set(this.handoverCountedAmounts, invoice.id, null)
            this.$set(this.handoverAmountErrors, invoice.id, '')
          })
        })

        if (!this.handoverPaidCashInvoices.length) {
          this.handoverInfoMessage = 'По этой брони нет оплаченных наличных счетов.'
          this.handoverInfoType = 'warning'
        } else if (!this.handoverEligibleInvoices.length) {
          if (this.handoverAlreadyMineInvoices.length) {
            this.handoverInfoMessage = 'Все наличные счета по этой брони уже оприходованы на вас.'
          } else {
            this.handoverInfoMessage = 'Нет счетов, доступных для оприходования (возможно, они уже в списке ниже).'
          }
          this.handoverInfoType = 'info'
        }
      } catch (error) {
        this.searchError = this.extractErrorMessage(error)
        this.reservation = null
        this.step = 'search'
      } finally {
        this.loader = false
      }
    },

    async finishHandover() {
      if (!this.handoverAccepted.length) return

      this.handoverFinishError = null
      this.loader = true
      try {
        const result = await Api.handoverCashInvoices({
          invoices: this.handoverAccepted.map(item => ({
            invoice_id: item.invoiceId,
            counted_amount: item.countedAmount,
          })),
        })
        this.handoverResult = result
        this.handoverAccepted = []
        this.reservation = null
        this.orderIdInput = ''
        this.step = 'done'
        localStorage.removeItem(HANDOVER_STORAGE_KEY)
      } catch (error) {
        this.handoverFinishError = this.extractErrorMessage(error)
      } finally {
        this.loader = false
      }
    },

    async searchReservation() {
      this.searchError = null
      this.loader = true
      try {
        const reservation = await Api.getReservationByOrderId({
          order_id: this.orderIdInput,
        })
        this.reservation = reservation
        this.amount = null
        this.paymentError = null
        this.step = 'payment'
      } catch (error) {
        this.searchError = this.extractErrorMessage(error)
      } finally {
        this.loader = false
      }
    },

    async payWithRetry(invoiceId, retries = 3) {
      for (let attempt = 0; attempt < retries; attempt += 1) {
        try {
          return await Api.payCashInvoiceWithReceipt({ invoice_id: invoiceId })
        } catch (error) {
          if (error?.response?.status === 409 && attempt < retries - 1) {
            await this.sleep(1000)
            continue
          }
          throw error
        }
      }
      return null
    },

    async pollReceiptStatus(invoiceId) {
      const deadline = Date.now() + POLL_TIMEOUT_MS
      while (Date.now() < deadline) {
        const receipt = await Api.checkReceipt(invoiceId)
        this.receiptResult = receipt
        const status = receipt.ofd_receipt_status

        if (status === 'fail' || status === 'FERMAERROR') {
          return status
        }
        if (status === 'done' && this.hasReceiptProof(receipt)) {
          return status
        }
        if (status && status !== 'wait' && status !== 'done') {
          return status
        }
        await this.sleep(POLL_INTERVAL_MS)
      }
      this.receiptResult = {
        ...this.receiptResult,
        ofd_receipt_status: 'timeout',
      }
      return 'timeout'
    },

    async refreshReceiptStatus() {
      if (!this.currentInvoiceId) return

      this.loader = true
      try {
        const receipt = await Api.checkReceipt(this.currentInvoiceId)
        this.receiptResult = receipt
        const status = receipt.ofd_receipt_status

        if (status === 'done' && this.hasReceiptProof(receipt)) {
          this.setResult('Реквизиты чека получены.', 'success')
        } else if (status === 'wait') {
          this.setResult('Чек ещё обрабатывается на ККТ.', 'info')
        } else if (status === 'done') {
          this.setResult('Чек выбит, реквизиты пока не пришли — попробуйте позже.', 'warning')
        } else {
          this.setResult(`Статус чека: ${this.receiptStatusLabel}`, 'info')
        }
      } catch (error) {
        this.setResult(this.extractErrorMessage(error), 'error')
      } finally {
        this.loader = false
      }
    },

    setResult(message, alertType) {
      this.resultMessage = message
      this.resultAlertType = alertType
      this.step = 'result'
      this.reloadReservation().catch(() => {})
    },

    async processFiscalization(invoiceId) {
      const payResult = await this.payWithRetry(invoiceId)
      this.receiptResult = payResult
      this.paymentAccepted = true

      let finalStatus = payResult.ofd_receipt_status

      if (finalStatus === 'wait' || (finalStatus === 'done' && !this.hasReceiptProof(payResult))) {
        finalStatus = await this.pollReceiptStatus(invoiceId)
      }

      if (finalStatus === 'done') {
        const message = this.hasReceiptProof(this.receiptResult)
          ? 'Оплата принята, чек успешно выбит.'
          : 'Оплата принята, чек выбит. Реквизиты ещё загружаются — нажмите «Обновить статус чека».'
        this.setResult(message, this.hasReceiptProof(this.receiptResult) ? 'success' : 'warning')
      } else if (finalStatus === 'fail' || finalStatus === 'FERMAERROR') {
        this.setResult(
          'Оплата принята, чек не выбит. Можно безопасно повторить отправку в ОФД — второй чек не создастся.',
          'warning',
        )
      } else if (finalStatus === 'timeout') {
        this.setResult(
          'Оплата принята. Статус чека не получен — нажмите «Обновить статус чека».',
          'warning',
        )
      } else {
        this.setResult('Оплата принята.', 'success')
      }
    },

    async acceptCashPayment() {
      if (!this.canAcceptPayment) return

      this.paymentError = null
      this.loader = true

      try {
        if (!this.currentInvoiceId) {
          const created = await Api.createCashInvoice({
            reservation_uuid: this.reservation.order_uuid,
            invoice_data: {
              payment_type: 1,
              cashbox: 2,
              payment_method: 2,
              name: INVOICE_NAME,
              price: this.amount,
              status: false,
              cash_register_tag: this.operatorTag,
            },
          })
          this.currentInvoiceId = created.id
        }

        await this.processFiscalization(this.currentInvoiceId)
      } catch (error) {
        if (this.currentInvoiceId && this.paymentAccepted) {
          this.setResult(this.extractErrorMessage(error), 'warning')
        } else if (this.currentInvoiceId) {
          this.paymentError = `${this.extractErrorMessage(error)} Счёт №${this.currentInvoiceId} создан — можно повторить отправку.`
        } else {
          this.paymentError = this.extractErrorMessage(error)
        }
      } finally {
        this.loader = false
      }
    },

    async retryFiscalization() {
      if (!this.currentInvoiceId) return

      this.loader = true
      try {
        await this.processFiscalization(this.currentInvoiceId)
      } catch (error) {
        this.setResult(this.extractErrorMessage(error), 'error')
      } finally {
        this.loader = false
      }
    },

    resetForm() {
      this.step = 'search'
      this.orderIdInput = ''
      this.reservation = null
      this.amount = null
      this.searchError = null
      this.paymentError = null
      this.receiptResult = null
      this.resultMessage = ''
      this.resultAlertType = 'info'
      this.currentInvoiceId = null
      this.paymentAccepted = false
    },
  },
}
</script>
